import React, { useState } from "react";
import { Dialog, DialogTitle, DialogContent, Button, TextField, Box, Typography, Grid, MenuItem, Avatar, IconButton, FormHelperText, DialogContentText, DialogActions, CircularProgress } from "@mui/material";
import { Formik } from "formik";
import * as Yup from "yup";
import { useParams } from "react-router-dom";
import moment from "moment/moment";
import { useDispatch } from "store";
import { openSnackbar, closeSnackbar } from "store/slices/snackbar";
import Select from "react-select";
import { useUpdateLeadsMutation } from "store/slices/apis/leadsApiSlice";
import CalenderSVG from "assets/logo/Calendar";
import { RxCross2 } from "react-icons/rx";

const rightLabelStyle = {
    fontWeight: 500,
    fontSize: "16px",
    lineHeight: "100%",
    color: "#5F5F5F",
    fontFamily: "Outfit, sans-serif",
};

const rightTextStyle = {
    "& .MuiInputBase-input": {
        fontWeight: 400,
        fontSize: "16px",
        lineHeight: "100%",
        letterSpacing: "0.16px",
        color: "#0A0A0C",
        fontFamily: "Outfit, sans-serif",
    },
};

const labelTextStyle = {
    fontWeight: 500,
    fontSize: "16px",
    lineHeight: "100%",
    color: "#5F5F5F",
    fontFamily: "Outfit, sans-serif",
    mb: 1,
    mt: 1,
};

const EditLeads = (props) => {
    const { open, handleClose, rowData, typeOptions, statusOptions, currentStageOptions, proposalStatusOptions } = props;
    const { projectId } = useParams();
    const dispatch = useDispatch();

    const [updateLeads, { isLoading }] = useUpdateLeadsMutation();

    const [formData, setFormData] = useState({
        name: rowData?.name || "",
        assetName: rowData?.assetName || "",
        assetType: rowData?.assetType || "",
        area: rowData?.area || "",
        expectationRentalRate: rowData?.expectationRentalRate || "",
        status: rowData?.status || "",
        type: rowData?.type || "",
        siteVisitDate: rowData?.siteVisitDate
            ? moment.utc(rowData.siteVisitDate).format("YYYY-MM-DD")
            : null,
        startDate: rowData?.startDate
            ? moment.utc(rowData.startDate).format("YYYY-MM-DD")
            : null,
        source: rowData?.source || "",
        proposalStatus: rowData?.proposalStatus || "",
        proposalDate: rowData?.proposalDate
            ? moment.utc(rowData.proposalDate).format("YYYY-MM-DD")
            : null,
        proposedRentalBase: rowData?.proposedRentalBase || "",
        proposedRentalCam: rowData?.proposedRentalCam || "",
        proposedRentalTi: rowData?.proposedRentalTi || "",
        currentStage: rowData?.currentStage || "",
        loiDate: rowData?.loiDate
            ? moment.utc(rowData.loiDate).format("YYYY-MM-DD")
            : null,
        timeToDeal: rowData?.timeToDeal || "",
        lastAction: rowData?.lastAction || "",
        nextSteps: rowData?.nextSteps || "",
        projectId: projectId,
    });

    const numericField = (fieldName) =>
        Yup.string()
            .nullable()
            .notRequired()
            .test(
                "is-valid-number",
                `${fieldName} must contain only numbers and decimals (no commas or spaces)`,
                (value) => {
                    if (!value) return true;
                    return /^\d+(\.\d+)?$/.test(value);
                }
            );

    const validationSchema = Yup.object({
        name: Yup.string().required("Name field is required"),

        area: numericField("Area"),
        expectationRentalRate: numericField("Expectation Rental Rate"),
        proposedRentalBase: numericField("Proposed Rental Base"),
        proposedRentalCam: numericField("Proposed Rental CAM"),
        proposedRentalTi: numericField("Proposed Rental TI"),

        timeToDeal: Yup.number()
            .typeError("Time To Deal must be a number")
            .required("Time To Deal is required")
            .integer("Must be a whole number"),
    });

    const handleSubmit = async (values, { setSubmitting, resetForm }) => {

        let reqBody = {
            leadId: rowData?.leadId,
            projectId: projectId,
            name: values?.name,
            assetName: values?.assetName,
            assetType: values?.assetType,
            area: values?.area ? values?.area.toString().trim() : 0,
            expectationRentalRate: values?.expectationRentalRate ? values?.expectationRentalRate.toString().trim() : 0,
            status: values?.status,
            type: values?.type,
            siteVisitDate: values?.siteVisitDate
                ? moment.utc(values?.siteVisitDate, 'YYYY-MM-DD').format('YYYY-MM-DDTHH:mm:ss[Z]')
                : null,
            startDate: values?.startDate
                ? moment.utc(values?.startDate, 'YYYY-MM-DD').format('YYYY-MM-DDTHH:mm:ss[Z]')
                : null,
            source: values?.source,
            proposalStatus: values?.proposalStatus,
            proposalDate: values?.proposalDate
                ? moment.utc(values?.proposalDate, 'YYYY-MM-DD').format('YYYY-MM-DDTHH:mm:ss[Z]')
                : null,
            proposedRentalBase: values?.proposedRentalBase ? values?.proposedRentalBase.toString().trim() : 0,
            proposedRentalCam: values?.proposedRentalCam ? values?.proposedRentalCam.toString().trim() : 0,
            proposedRentalTi: values?.proposedRentalTi ? values?.proposedRentalTi.toString().trim() : 0,

            currentStage: values?.currentStage,
            loiDate: values?.loiDate
                ? moment.utc(values?.loiDate, 'YYYY-MM-DD').format('YYYY-MM-DDTHH:mm:ss[Z]')
                : null,
            timeToDeal: values?.timeToDeal,
            lastAction: values?.lastAction,
            nextSteps: values?.nextSteps,
        };

        try {
            const response = await updateLeads(reqBody).unwrap();
            openNotification("Lead updated successfully", "success");
            resetForm();
            setSubmitting(false);
            handleClose();
        } catch (error) {
            // openNotification(error?.error ?? "Something went wrong", "error");
            //console.log("API ERROR:", error);

            let errorMessage = "Something went wrong";

            if (error?.data?.errors) {
                errorMessage = Object.values(error.data.errors)
                    .flat()
                    .join(", ");
            } else if (error?.data?.title) {
                errorMessage = error.data.title;
            } else if (error?.error) {
                errorMessage = error.error;
            }
            openNotification(errorMessage, "error");
        } finally {
            setSubmitting(false);
        }
    };

    const openNotification = (msg, type = "error", duration = 3000) => {
        dispatch(
            openSnackbar({
                open: true,
                message: msg,
                variant: 'alert',
                alert: {
                    color: type
                },
                close: false
            })
        );
        setTimeout(() => dispatch(closeSnackbar()), duration);
    };

    return (
        <>
            <Dialog open={open}
                fullWidth
                maxWidth="md"
                PaperProps={{
                    sx: { borderTopLeftRadius: "5px", borderTopRightRadius: "5px", backgroundColor: "#FFFFFF", width: "100%", overflowX: "hidden" },
                }}
                disableEscapeKeyDown
                onClose={(event, reason) => {
                    if (reason !== "backdropClick") {
                        handleClose();
                    }
                }}
            >
                <DialogTitle sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    borderBottom: "1px solid #ddd",
                    backgroundColor: "#ECECEC",
                    pb: 2,
                    pt: 3,
                    width: "100%",
                }}>
                    <Box>
                        <Typography sx={{ fontWeight: 600, fontSize: "27px", lineHeight: "100%", color: "#140F36", fontFamily: "Outfit, sans-serif" }}>
                            Edit Lead
                        </Typography>
                    </Box>
                    <IconButton
                        aria-label="close"
                        onClick={() => handleClose()}
                        sx={(theme) => ({
                            position: "absolute",
                            right: 8,
                            top: 8,
                            color: theme.palette.grey[500],
                        })}
                    >
                        <RxCross2 style={{ fontSize: "30px", color: "#140F36" }} />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    <Formik
                        initialValues={formData}
                        validationSchema={validationSchema}
                        onSubmit={handleSubmit}
                    >
                        {({ values,
                            errors,
                            touched,
                            handleChange,
                            handleBlur,
                            handleSubmit,
                            isSubmitting,
                            setFieldValue,
                            setFieldTouched,
                            setTouched, }) => (
                            <form onSubmit={handleSubmit}>
                                <Grid container spacing={2}>

                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <Typography sx={labelTextStyle}>Name</Typography>
                                        <TextField
                                            fullWidth
                                            name="name"
                                            value={values.name}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            placeholder="Enter Name"
                                            InputProps={{
                                                sx: {
                                                    height: "40px",
                                                    borderRadius: "8px",
                                                    backgroundColor: "#fff",
                                                    "& input::placeholder": {
                                                        fontWeight: 400,
                                                        fontSize: "16px",
                                                        lineHeight: "100%",
                                                        letterSpacing: "0.16px",
                                                        color: "#0A0A0C",
                                                        fontFamily: "Outfit, sans-serif",
                                                        opacity: 1,
                                                    },
                                                    "& input": {
                                                        fontWeight: 400,
                                                        fontSize: "16px",
                                                        lineHeight: "100%",
                                                        letterSpacing: "0.16px",
                                                        color: "#0A0A0C",
                                                        fontFamily: "Outfit, sans-serif",
                                                    },
                                                },
                                            }}
                                        />
                                        {touched.name && errors.name && (
                                            <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                {errors.name}
                                            </Typography>
                                        )}
                                    </Grid>

                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <Typography sx={labelTextStyle}>Asset Name</Typography>
                                        <TextField
                                            fullWidth
                                            name="assetName"
                                            value={values.assetName}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            placeholder="Enter Asset Name"
                                            InputProps={{
                                                sx: {
                                                    height: "40px",
                                                    borderRadius: "8px",
                                                    backgroundColor: "#fff",
                                                    "& input::placeholder": {
                                                        fontWeight: 400,
                                                        fontSize: "16px",
                                                        lineHeight: "100%",
                                                        letterSpacing: "0.16px",
                                                        color: "#0A0A0C",
                                                        fontFamily: "Outfit, sans-serif",
                                                        opacity: 1,
                                                    },
                                                    "& input": {
                                                        fontWeight: 400,
                                                        fontSize: "16px",
                                                        lineHeight: "100%",
                                                        letterSpacing: "0.16px",
                                                        color: "#0A0A0C",
                                                        fontFamily: "Outfit, sans-serif",
                                                    },
                                                },
                                            }}
                                        />
                                        {touched.assetName && errors.assetName && (
                                            <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                {errors.assetName}
                                            </Typography>
                                        )}
                                    </Grid>

                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <Typography sx={labelTextStyle}>Asset Type</Typography>
                                        <TextField
                                            fullWidth
                                            name="assetType"
                                            value={values.assetType}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            placeholder="Enter Asset Type"
                                            InputProps={{
                                                sx: {
                                                    height: "40px",
                                                    borderRadius: "8px",
                                                    backgroundColor: "#fff",
                                                    "& input::placeholder": {
                                                        fontWeight: 400,
                                                        fontSize: "16px",
                                                        lineHeight: "100%",
                                                        letterSpacing: "0.16px",
                                                        color: "#0A0A0C",
                                                        fontFamily: "Outfit, sans-serif",
                                                        opacity: 1,
                                                    },
                                                    "& input": {
                                                        fontWeight: 400,
                                                        fontSize: "16px",
                                                        lineHeight: "100%",
                                                        letterSpacing: "0.16px",
                                                        color: "#0A0A0C",
                                                        fontFamily: "Outfit, sans-serif",
                                                    },
                                                },
                                            }}
                                        />
                                        {touched.assetType && errors.assetType && (
                                            <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                {errors.assetType}
                                            </Typography>
                                        )}
                                    </Grid>

                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <Typography sx={labelTextStyle}>Area In Sqft</Typography>
                                        <TextField
                                            fullWidth
                                            name="area"
                                            value={values.area}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            placeholder="Enter Area In Sqft"
                                            InputProps={{
                                                sx: {
                                                    height: "40px",
                                                    borderRadius: "8px",
                                                    backgroundColor: "#fff",
                                                    "& input::placeholder": {
                                                        fontWeight: 400,
                                                        fontSize: "16px",
                                                        lineHeight: "100%",
                                                        letterSpacing: "0.16px",
                                                        color: "#0A0A0C",
                                                        fontFamily: "Outfit, sans-serif",
                                                        opacity: 1,
                                                    },
                                                    "& input": {
                                                        fontWeight: 400,
                                                        fontSize: "16px",
                                                        lineHeight: "100%",
                                                        letterSpacing: "0.16px",
                                                        color: "#0A0A0C",
                                                        fontFamily: "Outfit, sans-serif",
                                                    },
                                                },
                                            }}
                                        />
                                        {touched.area && errors.area && (
                                            <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                {errors.area}
                                            </Typography>
                                        )}
                                    </Grid>

                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <Typography sx={labelTextStyle}>Expectation Rental Rate</Typography>
                                        <TextField
                                            fullWidth
                                            name="expectationRentalRate"
                                            value={values.expectationRentalRate}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            placeholder="Enter Expectation Rental Rate"
                                            InputProps={{
                                                sx: {
                                                    height: "40px",
                                                    borderRadius: "8px",
                                                    backgroundColor: "#fff",
                                                    "& input::placeholder": {
                                                        fontWeight: 400,
                                                        fontSize: "16px",
                                                        lineHeight: "100%",
                                                        letterSpacing: "0.16px",
                                                        color: "#0A0A0C",
                                                        fontFamily: "Outfit, sans-serif",
                                                        opacity: 1,
                                                    },
                                                    "& input": {
                                                        fontWeight: 400,
                                                        fontSize: "16px",
                                                        lineHeight: "100%",
                                                        letterSpacing: "0.16px",
                                                        color: "#0A0A0C",
                                                        fontFamily: "Outfit, sans-serif",
                                                    },
                                                },
                                            }}
                                        />
                                        {touched.expectationRentalRate && errors.expectationRentalRate && (
                                            <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                {errors.expectationRentalRate}
                                            </Typography>
                                        )}
                                    </Grid>

                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <Typography sx={labelTextStyle}>Status</Typography>
                                        <Select
                                            name="status"
                                            options={statusOptions}
                                            value={statusOptions.find(opt => opt.value === String(values.status)) || null}
                                            onChange={(option) => setFieldValue("status", option.value)}
                                            onBlur={() => setFieldTouched("status", true)}
                                            placeholder="Select Status"
                                        />
                                        {touched.status && errors.status && (
                                            <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                {errors.status}
                                            </Typography>
                                        )}
                                    </Grid>

                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <Typography sx={labelTextStyle}>Type</Typography>
                                        <Select
                                            name="type"
                                            options={typeOptions}
                                            value={typeOptions.find(opt => opt.value === String(values.type)) || null}
                                            onChange={(option) => setFieldValue("type", option.value)}
                                            onBlur={() => setFieldTouched("type", true)}
                                            placeholder="Select Type"
                                        />
                                        {touched.type && errors.type && (
                                            <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                {errors.type}
                                            </Typography>
                                        )}
                                    </Grid>

                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <Typography sx={{ ...rightLabelStyle, mb: 1, mt: 1 }}>
                                            Site Visit Date
                                        </Typography>
                                        <TextField
                                            type="date"
                                            name="siteVisitDate"
                                            value={values.siteVisitDate}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            onClick={(e) => {
                                                const input = e.currentTarget.parentElement?.querySelector('input[type="date"]');
                                                if (input) input.showPicker?.();
                                            }}
                                            fullWidth
                                            sx={{
                                                ...rightTextStyle,
                                                "& input::-webkit-calendar-picker-indicator": {
                                                    display: "none",
                                                    WebkitAppearance: "none",
                                                },
                                                "& input[type='date']": {
                                                    appearance: "none",
                                                    WebkitAppearance: "none",
                                                },
                                                "& .MuiOutlinedInput-root": {
                                                    "& fieldset": {
                                                        border: "none",
                                                    },
                                                },
                                            }}
                                            InputProps={{
                                                startAdornment: (
                                                    <Box sx={{ ml: 2, mr: 1, color: "#433F5E", display: "flex", alignItems: "center", cursor: "pointer" }}>
                                                        {CalenderSVG}
                                                    </Box>
                                                ),
                                                sx: {
                                                    backgroundColor: "#fff",
                                                    border: '1px solid',
                                                    borderColor: 'hsl(0, 0%, 80%)',
                                                    height: "40px",
                                                    width: { xs: "100%", md: "100%" },
                                                    borderRadius: "5px",
                                                },
                                            }}
                                        />
                                        {touched.siteVisitDate && errors.siteVisitDate && (
                                            <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                {errors.siteVisitDate}
                                            </Typography>
                                        )}
                                    </Grid>

                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <Typography sx={{ ...rightLabelStyle, mb: 1, mt: 1 }}>
                                            Start Date
                                        </Typography>
                                        <TextField
                                            type="date"
                                            name="startDate"
                                            value={values.startDate}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            onClick={(e) => {
                                                const input = e.currentTarget.parentElement?.querySelector('input[type="date"]');
                                                if (input) input.showPicker?.();
                                            }}
                                            fullWidth
                                            sx={{
                                                ...rightTextStyle,
                                                "& input::-webkit-calendar-picker-indicator": {
                                                    display: "none",
                                                    WebkitAppearance: "none",
                                                },
                                                "& input[type='date']": {
                                                    appearance: "none",
                                                    WebkitAppearance: "none",
                                                },
                                                "& .MuiOutlinedInput-root": {
                                                    "& fieldset": {
                                                        border: "none",
                                                    },
                                                },
                                            }}
                                            InputProps={{
                                                startAdornment: (
                                                    <Box sx={{ ml: 2, mr: 1, color: "#433F5E", display: "flex", alignItems: "center", cursor: "pointer" }}>
                                                        {CalenderSVG}
                                                    </Box>
                                                ),
                                                sx: {
                                                    backgroundColor: "#fff",
                                                    border: '1px solid',
                                                    borderColor: 'hsl(0, 0%, 80%)',
                                                    height: "40px",
                                                    width: { xs: "100%", md: "100%" },
                                                    borderRadius: "5px",
                                                },
                                            }}
                                        />
                                        {touched.startDate && errors.startDate && (
                                            <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                {errors.startDate}
                                            </Typography>
                                        )}
                                    </Grid>

                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <Typography sx={labelTextStyle}>Source</Typography>
                                        <TextField
                                            fullWidth
                                            name="source"
                                            value={values.source}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            placeholder="Enter Source"
                                            InputProps={{
                                                sx: {
                                                    height: "40px",
                                                    borderRadius: "8px",
                                                    backgroundColor: "#fff",
                                                    "& input::placeholder": {
                                                        fontWeight: 400,
                                                        fontSize: "16px",
                                                        lineHeight: "100%",
                                                        letterSpacing: "0.16px",
                                                        color: "#0A0A0C",
                                                        fontFamily: "Outfit, sans-serif",
                                                        opacity: 1,
                                                    },
                                                    "& input": {
                                                        fontWeight: 400,
                                                        fontSize: "16px",
                                                        lineHeight: "100%",
                                                        letterSpacing: "0.16px",
                                                        color: "#0A0A0C",
                                                        fontFamily: "Outfit, sans-serif",
                                                    },
                                                },
                                            }}
                                        />
                                        {touched.source && errors.source && (
                                            <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                {errors.source}
                                            </Typography>
                                        )}
                                    </Grid>

                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <Typography sx={labelTextStyle}>Proposal Status</Typography>
                                        <Select
                                            name="proposalStatus"
                                            options={proposalStatusOptions}
                                            value={proposalStatusOptions.find(opt => opt.value === String(values.proposalStatus)) || null}
                                            onChange={(option) => setFieldValue("proposalStatus", option.value)}
                                            onBlur={() => setFieldTouched("proposalStatus", true)}
                                            placeholder="Select Proposal Status"
                                        />
                                        {touched.proposalStatus && errors.proposalStatus && (
                                            <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                {errors.proposalStatus}
                                            </Typography>
                                        )}
                                    </Grid>

                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <Typography sx={{ ...rightLabelStyle, mb: 1, mt: 1 }}>
                                            Proposal Date
                                        </Typography>
                                        <TextField
                                            type="date"
                                            name="proposalDate"
                                            value={values.proposalDate}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            onClick={(e) => {
                                                const input = e.currentTarget.parentElement?.querySelector('input[type="date"]');
                                                if (input) input.showPicker?.();
                                            }}
                                            fullWidth
                                            sx={{
                                                ...rightTextStyle,
                                                "& input::-webkit-calendar-picker-indicator": {
                                                    display: "none",
                                                    WebkitAppearance: "none",
                                                },
                                                "& input[type='date']": {
                                                    appearance: "none",
                                                    WebkitAppearance: "none",
                                                },
                                                "& .MuiOutlinedInput-root": {
                                                    "& fieldset": {
                                                        border: "none",
                                                    },
                                                },
                                            }}
                                            InputProps={{
                                                startAdornment: (
                                                    <Box sx={{ ml: 2, mr: 1, color: "#433F5E", display: "flex", alignItems: "center", cursor: "pointer" }}>
                                                        {CalenderSVG}
                                                    </Box>
                                                ),
                                                sx: {
                                                    backgroundColor: "#fff",
                                                    border: '1px solid',
                                                    borderColor: 'hsl(0, 0%, 80%)',
                                                    height: "40px",
                                                    width: { xs: "100%", md: "100%" },
                                                    borderRadius: "5px",
                                                },
                                            }}
                                        />
                                        {touched.proposalDate && errors.proposalDate && (
                                            <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                {errors.proposalDate}
                                            </Typography>
                                        )}
                                    </Grid>

                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <Typography sx={labelTextStyle}>Proposed Rental Base</Typography>
                                        <TextField
                                            fullWidth
                                            name="proposedRentalBase"
                                            value={values.proposedRentalBase}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            placeholder="Enter Proposed Rental Base"
                                            InputProps={{
                                                sx: {
                                                    height: "40px",
                                                    borderRadius: "8px",
                                                    backgroundColor: "#fff",
                                                    "& input::placeholder": {
                                                        fontWeight: 400,
                                                        fontSize: "16px",
                                                        lineHeight: "100%",
                                                        letterSpacing: "0.16px",
                                                        color: "#0A0A0C",
                                                        fontFamily: "Outfit, sans-serif",
                                                        opacity: 1,
                                                    },
                                                    "& input": {
                                                        fontWeight: 400,
                                                        fontSize: "16px",
                                                        lineHeight: "100%",
                                                        letterSpacing: "0.16px",
                                                        color: "#0A0A0C",
                                                        fontFamily: "Outfit, sans-serif",
                                                    },
                                                },
                                            }}
                                        />
                                        {touched.proposedRentalBase && errors.proposedRentalBase && (
                                            <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                {errors.proposedRentalBase}
                                            </Typography>
                                        )}
                                    </Grid>

                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <Typography sx={labelTextStyle}>Proposed Rental CAM</Typography>
                                        <TextField
                                            fullWidth
                                            name="proposedRentalCam"
                                            value={values.proposedRentalCam}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            placeholder="Enter Proposed Rental CAM"
                                            InputProps={{
                                                sx: {
                                                    height: "40px",
                                                    borderRadius: "8px",
                                                    backgroundColor: "#fff",
                                                    "& input::placeholder": {
                                                        fontWeight: 400,
                                                        fontSize: "16px",
                                                        lineHeight: "100%",
                                                        letterSpacing: "0.16px",
                                                        color: "#0A0A0C",
                                                        fontFamily: "Outfit, sans-serif",
                                                        opacity: 1,
                                                    },
                                                    "& input": {
                                                        fontWeight: 400,
                                                        fontSize: "16px",
                                                        lineHeight: "100%",
                                                        letterSpacing: "0.16px",
                                                        color: "#0A0A0C",
                                                        fontFamily: "Outfit, sans-serif",
                                                    },
                                                },
                                            }}
                                        />
                                        {touched.proposedRentalCam && errors.proposedRentalCam && (
                                            <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                {errors.proposedRentalCam}
                                            </Typography>
                                        )}
                                    </Grid>

                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <Typography sx={labelTextStyle}>Proposed Rental TI</Typography>
                                        <TextField
                                            fullWidth
                                            name="proposedRentalTi"
                                            value={values.proposedRentalTi}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            placeholder="Enter Proposed Rental TI"
                                            InputProps={{
                                                sx: {
                                                    height: "40px",
                                                    borderRadius: "8px",
                                                    backgroundColor: "#fff",
                                                    "& input::placeholder": {
                                                        fontWeight: 400,
                                                        fontSize: "16px",
                                                        lineHeight: "100%",
                                                        letterSpacing: "0.16px",
                                                        color: "#0A0A0C",
                                                        fontFamily: "Outfit, sans-serif",
                                                        opacity: 1,
                                                    },
                                                    "& input": {
                                                        fontWeight: 400,
                                                        fontSize: "16px",
                                                        lineHeight: "100%",
                                                        letterSpacing: "0.16px",
                                                        color: "#0A0A0C",
                                                        fontFamily: "Outfit, sans-serif",
                                                    },
                                                },
                                            }}
                                        />
                                        {touched.proposedRentalTi && errors.proposedRentalTi && (
                                            <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                {errors.proposedRentalTi}
                                            </Typography>
                                        )}
                                    </Grid>

                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <Typography sx={labelTextStyle}>Current Stage</Typography>
                                        <Select
                                            name="currentStage"
                                            options={currentStageOptions}
                                            value={currentStageOptions.find(opt => opt.value === String(values.currentStage)) || null}
                                            onChange={(option) => setFieldValue("currentStage", option.value)}
                                            onBlur={() => setFieldTouched("currentStage", true)}
                                            placeholder="Select Current Stage"
                                        />
                                        {touched.currentStage && errors.currentStage && (
                                            <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                {errors.currentStage}
                                            </Typography>
                                        )}
                                    </Grid>

                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <Typography sx={{ ...rightLabelStyle, mb: 1, mt: 1 }}>
                                            LOI Date
                                        </Typography>
                                        <TextField
                                            type="date"
                                            name="loiDate"
                                            value={values.loiDate}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            onClick={(e) => {
                                                const input = e.currentTarget.parentElement?.querySelector('input[type="date"]');
                                                if (input) input.showPicker?.();
                                            }}
                                            fullWidth
                                            sx={{
                                                ...rightTextStyle,
                                                "& input::-webkit-calendar-picker-indicator": {
                                                    display: "none",
                                                    WebkitAppearance: "none",
                                                },
                                                "& input[type='date']": {
                                                    appearance: "none",
                                                    WebkitAppearance: "none",
                                                },
                                                "& .MuiOutlinedInput-root": {
                                                    "& fieldset": {
                                                        border: "none",
                                                    },
                                                },
                                            }}
                                            InputProps={{
                                                startAdornment: (
                                                    <Box sx={{ ml: 2, mr: 1, color: "#433F5E", display: "flex", alignItems: "center", cursor: "pointer" }}>
                                                        {CalenderSVG}
                                                    </Box>
                                                ),
                                                sx: {
                                                    backgroundColor: "#fff",
                                                    border: '1px solid',
                                                    borderColor: 'hsl(0, 0%, 80%)',
                                                    height: "40px",
                                                    width: { xs: "100%", md: "100%" },
                                                    borderRadius: "5px",
                                                },
                                            }}
                                        />
                                        {touched.loiDate && errors.loiDate && (
                                            <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                {errors.loiDate}
                                            </Typography>
                                        )}
                                    </Grid>

                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <Typography sx={labelTextStyle}>Time to Deal</Typography>
                                        <TextField
                                            fullWidth
                                            name="timeToDeal"
                                            value={values.timeToDeal}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            placeholder="Enter Time to Deal"
                                            InputProps={{
                                                sx: {
                                                    height: "40px",
                                                    borderRadius: "8px",
                                                    backgroundColor: "#fff",
                                                    "& input::placeholder": {
                                                        fontWeight: 400,
                                                        fontSize: "16px",
                                                        lineHeight: "100%",
                                                        letterSpacing: "0.16px",
                                                        color: "#0A0A0C",
                                                        fontFamily: "Outfit, sans-serif",
                                                        opacity: 1,
                                                    },
                                                    "& input": {
                                                        fontWeight: 400,
                                                        fontSize: "16px",
                                                        lineHeight: "100%",
                                                        letterSpacing: "0.16px",
                                                        color: "#0A0A0C",
                                                        fontFamily: "Outfit, sans-serif",
                                                    },
                                                },
                                            }}
                                        />
                                        {touched.timeToDeal && errors.timeToDeal && (
                                            <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                {errors.timeToDeal}
                                            </Typography>
                                        )}
                                    </Grid>

                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <Typography sx={labelTextStyle}>Last Action</Typography>
                                        <TextField
                                            fullWidth
                                            name="lastAction"
                                            value={values.lastAction}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            placeholder="Enter Last Action"
                                            InputProps={{
                                                sx: {
                                                    height: "40px",
                                                    borderRadius: "8px",
                                                    backgroundColor: "#fff",
                                                    "& input::placeholder": {
                                                        fontWeight: 400,
                                                        fontSize: "16px",
                                                        lineHeight: "100%",
                                                        letterSpacing: "0.16px",
                                                        color: "#0A0A0C",
                                                        fontFamily: "Outfit, sans-serif",
                                                        opacity: 1,
                                                    },
                                                    "& input": {
                                                        fontWeight: 400,
                                                        fontSize: "16px",
                                                        lineHeight: "100%",
                                                        letterSpacing: "0.16px",
                                                        color: "#0A0A0C",
                                                        fontFamily: "Outfit, sans-serif",
                                                    },
                                                },
                                            }}
                                        />
                                        {touched.lastAction && errors.lastAction && (
                                            <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                {errors.lastAction}
                                            </Typography>
                                        )}
                                    </Grid>

                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <Typography sx={labelTextStyle}>Next Steps</Typography>
                                        <TextField
                                            fullWidth
                                            name="nextSteps"
                                            value={values.nextSteps}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            placeholder="Enter Next Steps"
                                            InputProps={{
                                                sx: {
                                                    height: "40px",
                                                    borderRadius: "8px",
                                                    backgroundColor: "#fff",
                                                    "& input::placeholder": {
                                                        fontWeight: 400,
                                                        fontSize: "16px",
                                                        lineHeight: "100%",
                                                        letterSpacing: "0.16px",
                                                        color: "#0A0A0C",
                                                        fontFamily: "Outfit, sans-serif",
                                                        opacity: 1,
                                                    },
                                                    "& input": {
                                                        fontWeight: 400,
                                                        fontSize: "16px",
                                                        lineHeight: "100%",
                                                        letterSpacing: "0.16px",
                                                        color: "#0A0A0C",
                                                        fontFamily: "Outfit, sans-serif",
                                                    },
                                                },
                                            }}
                                        />
                                        {touched.nextSteps && errors.nextSteps && (
                                            <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                {errors.nextSteps}
                                            </Typography>
                                        )}
                                    </Grid>

                                    <Grid size={12}>
                                        <Box
                                            sx={{
                                                display: "flex",
                                                justifyContent: "flex-end",
                                                alignItems: "center",
                                            }}
                                        >
                                            <Button onClick={handleClose}
                                                sx={{
                                                    textTransform: "none",
                                                    color: "#F15744",
                                                    fontSize: "14px",
                                                    lineHeight: "100%",
                                                    fontFamily: "Outfit, sans-serif",
                                                    borderRadius: "25px",
                                                    fontWeight: 500,
                                                    px: "20px",
                                                    py: "15px",
                                                }}>
                                                Cancel
                                            </Button>
                                            <Button
                                                type="submit"
                                                variant="contained"
                                                // loading={isLoading}
                                                sx={{
                                                    backgroundColor: "#00344C",
                                                    borderRadius: "25px",
                                                    textTransform: "none",
                                                    fontWeight: 500,
                                                    fontSize: "14px",
                                                    lineHeight: "100%",
                                                    fontFamily: "Outfit, sans-serif",
                                                    color: "#FFFFFF",
                                                    px: "20px",
                                                    py: "15px",
                                                    display: "flex",
                                                    gap: 1,
                                                    "&.Mui-disabled": {
                                                        backgroundColor: "#00344C",
                                                        color: "#fff",
                                                        opacity: 0.7,
                                                    },
                                                }}
                                                disabled={isLoading}
                                            >
                                                {isLoading && <CircularProgress size={16} color="inherit" />}
                                                Save
                                            </Button>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </form>
                        )}
                    </Formik>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default EditLeads;